<script setup lang="ts">
import { inject,Ref,ref, computed, onMounted } from 'vue'
import { CheckCircle, XCircle, Clock, Zap, Loader2 } from 'lucide-vue-next'
import clientTransfer from '@/services/clientTransfer'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import type { TransferItem } from '@/types/Transfer'

type TransferStatus = 'success' | 'failure' | 'queued' | 'transferring'

interface StatusCount {
  success: number
  failure: number
  queued: number
  transferring: number
}

interface ProviderItem {
  provider: string
  items: {
    label: string
    value: number
    status: TransferStatus
  }[]
}

const props = defineProps<{
  size?: 'small' | 'medium' | 'large'
  height?: number
}>()
const divHeight = computed(() => {
	if (props.height) {
		if (props.size === 'small') {
			return (props.height * 100) + 'px';
		} else if (props.size === 'medium') {
			return (props.height * 200) + 'px';
		} else if (props.size === 'large') {
			return (props.height * 400) + 'px';
		}
	}
	return '100px';
});

const { transferSummary, getClientTransferSummary } = clientTransfer()
const vhostId = inject<Ref<string>>('vhostId', ref("0"))
const isLoading = ref(false)

const normalizeStatus = (status: string): TransferStatus => {
  const statusMap: Record<string, TransferStatus> = {
    'completed': 'success',
    'success': 'success',
    'failed': 'failure',
    'error': 'failure',
    'queued': 'queued',
    'processing': 'transferring',
    'transferring': 'transferring'
  }
  return statusMap[status.toLowerCase()] || 'queued'
}

const loadData = async () => {
  isLoading.value = true
  try {
    await getClientTransferSummary(vhostId.value)
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  loadData()
})

const providerItems = computed<ProviderItem[]>(() => {
  if (!transferSummary.value) return []
  
  const groups: Record<string, StatusCount> = {}

  transferSummary.value.forEach((item: TransferItem) => {
    if (!groups[item.destination]) {
      groups[item.destination] = {
        success: 0,
        failure: 0,
        queued: 0,
        transferring: 0
      }
    }
    
    const normalizedStatus = normalizeStatus(item.status)
    groups[item.destination][normalizedStatus]++
  })

  return Object.entries(groups).map(([provider, counts]) => ({
    provider,
    items: [
      { label: 'Successful', value: counts.success, status: 'success' },
      { label: 'Failed', value: counts.failure, status: 'failure' },
      { label: 'Queued', value: counts.queued, status: 'queued' },
      { label: 'Transferring', value: counts.transferring, status: 'transferring' }
    ]
  }))
})

const getStatusIcon = (status: TransferStatus) => {
  const icons = {
    success: CheckCircle,
    failure: XCircle,
    queued: Clock,
    transferring: Zap,
    processing: Zap
  }
  return icons[status]
}

const getStatusColor = (status: TransferStatus) => {
  const colors = {
    success: 'text-success',
    failure: 'text-destructive',
    queued: 'text-muted-foreground',
    transferring: 'text-primary',
    processing: 'text-primary'
  }
  return colors[status]
}
</script>

<template>

  <Card  :style="'height:'+divHeight" class="rounded-2xl shadow-sm border">
  <div v-if="isLoading" class="flex flex-col h-full w-full justify-center items-center p-8">
    <Loader2 class="w-8 h-8 animate-spin text-primary" />
  </div>
  <div v-else>
	  <div v-if="providerItems.length==0" class="flex flex-col w-full h-full justify-center items-center" >
		  <h2 class="text-title text-muted">No Transfers Found.</h2>
	  </div>
    <Tabs v-else :default-value="providerItems[0]?.provider">
      <CardHeader class="border-b p-0">
        <TabsList class="w-full p-0">
          <TabsTrigger v-for="(provider, index) in providerItems" :key="index" :value="provider.provider" class="flex-1 py-3 px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium transition-colors" :class="{ 'p-1 text-body-sm': size === 'small' }" >
            {{ provider.provider }}
          </TabsTrigger>
        </TabsList>
      </CardHeader>

      <CardContent class="bg-card p-2">
        <TabsContent v-for="(provider, index) in providerItems" :key="index" :value="provider.provider" >
          <div class="grid grid-cols-2 gap-3">
            <div v-for="(item, itemIndex) in provider.items" :key="itemIndex" class="flex items-center gap-3 bg-muted/50 px-4 py-0 rounded-lg" >
              <component :is="getStatusIcon(item.status)" class="w-5 h-5" :class="getStatusColor(item.status)" v-if="size!='small'"/>
		<div class="font-bold text-foreground" v-else>{{ item.value }}</div>
              <div>
                <div class="font-medium">{{ item.label }}</div>
		<div class="font-bold text-foreground" :class="{'hidden':size=='small'}">{{ item.value }}</div>
              </div>
            </div>
          </div>
        </TabsContent>
      </CardContent>
    </Tabs>
  </div>
  </Card>
</template>
